/**
 * Менеджер запуска симуляции боя
 * Вынесено из legacy-app.js для модульности
 */

/**
 * Создает менеджер запуска симуляции боя
 * @param {object} params - Параметры менеджера
 * @param {function} params.safeFetch - Функция безопасного fetch
 * @param {string} params.apiUrl - URL API
 * @param {function} params.buildAuthHeaders - Функция построения заголовков авторизации
 * @param {function} params.getTelegramWebAppSafe - Функция получения Telegram WebApp
 * @param {function} params.loadBattleData - Функция загрузки данных боя (wt)
 * @param {function} params.showBattleModal - Функция показа модального окна (ht)
 * @param {function} params.renderBattleView - Функция рендеринга вида боя (vt)
 * @param {function} params.renderBattleResult - Функция рендеринга результата боя (Bt)
 * @param {function} params.loadBattleHistory - Функция загрузки истории боев (Ye)
 * @param {function} params.checkErrorbornStatus - Функция проверки статуса Errorborn (Ee)
 * @param {function} params.renderErrorbornScreen - Функция рендеринга экрана Errorborn (qe)
 * @param {function} params.parseUserCreatureId - Функция парсинга ID существа (Ne)
 * @param {function} params.getUserName - Функция получения имени пользователя (zs)
 * @param {function} params.findAiPreset - Функция поиска AI пресета (Lt)
 * @param {function} params.cancelPvpSearch - Функция отмены поиска PvP (cn)
 * @param {function} params.clearBattleCountdown - Функция очистки countdown (Me)
 * @param {function} params.renderBattleView - Функция рендеринга вида боя (fe)
 * @param {array} params.collection - Коллекция существ (Y)
 * @param {array} params.aiTemplates - Шаблоны AI (bn)
 * @param {object} params.currentUser - Текущий пользователь (d)
 * @param {object} params.battleState - Состояние боя (w)
 * @returns {function} Функция запуска симуляции боя
 */
export function createBattleSimulationRunner({
  safeFetch,
  apiUrl,
  buildAuthHeaders,
  getTelegramWebAppSafe,
  loadBattleData,
  showBattleModal,
  renderBattleView,
  renderBattleResult,
  loadBattleHistory,
  checkErrorbornStatus,
  renderErrorbornScreen,
  parseUserCreatureId,
  getUserName,
  findAiPreset,
  cancelPvpSearch,
  clearBattleCountdown,
  collection,
  aiTemplates,
  currentUser,
  battleState,
}) {
  /**
   * Запускает симуляцию боя
   * 
   * Поддерживает две системы:
   * 1. Новая система формаций (window.__battleFormation) - для 1v1, 3v3, 5v5 с AI балансировкой
   * 2. Старая система выбора (селекты) - для обратной совместимости
   * 
   * @returns {Promise<void>}
   * @throws {Error} Если не удалось запустить симуляцию
   */
  async function runBattleSimulation() {
    // Проверяем, используется ли новая система формаций
    if (window.__battleFormation && window.__battleFormation.player && window.__battleFormation.player.length > 0) {
      // Используем новую систему формаций
      if (window.__battleFormation.queueId) {
        await cancelPvpSearch({ silent: true });
      }
      clearBattleCountdown();
      
      const makePublicCheckbox = document.getElementById("battle-make-public");
      const battleRequest = {
        arena: "THE_STACK",
        persist: true,
        public: makePublicCheckbox ? makePublicCheckbox.checked : false,
        combatants: [],
      };
      const ownerName = getUserName(currentUser);
      
      // Добавляем существ игрока из формации
      window.__battleFormation.player.forEach((creature) => {
        // Поддерживаем разные форматы данных
        const creatureId = creature.creatureId || creature.creature_id;
        const userCreatureId = creature.id || creature.userCreatureId || creature.user_creature_id;
        
        if (!creatureId) {
          console.error('[runBattleSimulation] Missing creatureId for creature:', creature);
          const app = getTelegramWebAppSafe();
          app?.showAlert?.('Ошибка: не удалось определить ID существа');
          return;
        }
        
        battleRequest.combatants.push({
          team: "A",
          userId: currentUser?.id || null,
          creatureId: creatureId,
          userCreatureId: userCreatureId,
          ownerName: ownerName,
        });
      });
      
      // Генерируем AI противников с балансировкой
      const slotCount = window.__battleFormation.mode === '1v1' ? 1 : window.__battleFormation.mode === '3v3' ? 3 : 5;
      
      // Доступные AI пресеты
      const availablePresets = aiTemplates.filter(preset => preset.combatant);
      
      // Проверка наличия AI пресетов
      if (availablePresets.length === 0) {
        console.error('[runBattleSimulation] No AI presets available');
        const app = getTelegramWebAppSafe();
        app?.showAlert?.('Ошибка: нет доступных AI противников');
        return;
      }
      
      // Выбираем случайные пресеты для противников
      for (let i = 0; i < slotCount; i++) {
        const randomPreset = availablePresets[Math.floor(Math.random() * availablePresets.length)];
        if (randomPreset && randomPreset.combatant) {
          battleRequest.combatants.push({
            team: "B",
            baseId: randomPreset.combatant.baseId,
            creature: randomPreset.combatant.creature,
          });
        }
      }
      
      // Запускаем бой
      try {
        console.log('[runBattleSimulation] Starting battle with formation:', {
          mode: window.__battleFormation.mode,
          playerCount: window.__battleFormation.player.length,
          combatants: battleRequest.combatants.length
        });
        
        const response = await safeFetch(`${apiUrl}/battle/run`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...buildAuthHeaders() },
          body: JSON.stringify(battleRequest),
        });
        
        if (!response.success) {
          console.error('[runBattleSimulation] API error:', response.error);
          const app = getTelegramWebAppSafe();
          app?.showAlert?.(response.error || "Ошибка запуска боя");
          return;
        }
        
        loadBattleData(response.data);
        showBattleModal();
        renderBattleResult(response.data);
        await loadBattleHistory();
        
        // Убеждаемся, что рендеринг выполнен после открытия модального окна
        // loadBattleData уже вызывает renderBattle() внутри, но для надежности вызываем renderBattleView
        setTimeout(() => {
          if (battleState && battleState.panels && battleState.panels.length > 0) {
            renderBattleView();
          } else {
            console.warn('[runBattleSimulation] Battle data not ready after loadBattleData, retrying...');
            // Повторная попытка через еще 100ms
            setTimeout(() => {
              if (battleState && battleState.panels && battleState.panels.length > 0) {
                renderBattleView();
              }
            }, 100);
          }
        }, 100);
        
        const errorbornStatus = await checkErrorbornStatus(true);
        const errorbornScreen = document.getElementById("errorborn-screen");
        if (errorbornScreen?.classList.contains("active") && errorbornStatus) {
          renderErrorbornScreen(errorbornStatus);
        }
      } catch (error) {
        console.error('[runBattleSimulation] Network error:', error);
        const app = getTelegramWebAppSafe();
        app?.showAlert?.("Ошибка подключения к серверу");
      }
      
      return;
    }
    
    // Старая система (без формаций)
    clearBattleCountdown();
    
    // Проверка коллекции существ только для старой системы
    if (!collection || collection.length === 0) {
      const app = getTelegramWebAppSafe();
      app?.showAlert?.('У вас нет существ в коллекции');
      return;
    }
    
    const playerSelect = document.getElementById("battle-my-creature");
    const enemySelect = document.getElementById("battle-enemy-creature");
    
    if (!playerSelect || !enemySelect) {
      const app = getTelegramWebAppSafe();
      app?.showAlert?.("Ошибка: не найдены элементы выбора существ");
      return;
    }
    
    const playerValue = playerSelect.value;
    const enemyValue = enemySelect.value;
    
    if (!playerValue || !playerValue.startsWith("user:")) {
      const app = getTelegramWebAppSafe();
      app?.showAlert?.("Выберите своего бойца");
      return;
    }
    
    const makePublicCheckbox = document.getElementById("battle-make-public");
    const battleRequest = {
      arena: "THE_STACK",
      persist: true,
      public: makePublicCheckbox ? makePublicCheckbox.checked : false,
      combatants: [],
    };
    const ownerName = getUserName(currentUser);
    
    // Добавляем существо игрока
    const { creatureId: playerCreatureId, userCreatureId: playerUserCreatureId } = parseUserCreatureId(playerValue);
    if (!playerUserCreatureId) {
      const app = getTelegramWebAppSafe();
      app?.showAlert?.("Ошибка: некорректный выбор бойца");
      return;
    }
    
    // Находим существо в коллекции для получения данных
    const playerCreature = collection.find(
      (c) => c.creatureId === playerCreatureId && c.id === playerUserCreatureId
    );
    
    battleRequest.combatants.push({
      team: "A",
      userId: currentUser?.id || null,
      creatureId: playerCreatureId,
      userCreatureId: playerUserCreatureId,
      ownerName: ownerName,
    });
    
    // Добавляем противника
    if (enemyValue.startsWith("user:")) {
      if (enemyValue === "user:random" && collection.length > 1) {
        // Исключаем существо игрока из случайного выбора
        let availableCreatures = collection.filter(
          (c) => !(playerCreature && c.id === playerCreature.id)
        );
        if (availableCreatures.length === 0) {
          availableCreatures = collection.slice();
        }
        const randomCreature = availableCreatures[Math.floor(Math.random() * availableCreatures.length)];
        battleRequest.combatants.push({
          team: "B",
          userId: currentUser?.id || null,
          creatureId: randomCreature.creatureId,
          userCreatureId: randomCreature.id,
        });
      } else {
        const { creatureId: enemyCreatureId, userCreatureId: enemyUserCreatureId } = parseUserCreatureId(enemyValue);
        battleRequest.combatants.push({
          team: "B",
          userId: currentUser?.id || null,
          creatureId: enemyCreatureId,
          userCreatureId: enemyUserCreatureId,
          ownerName: ownerName,
        });
      }
    } else {
      // AI пресет
      const aiPreset = findAiPreset(enemyValue) || findAiPreset("ai_bitcor");
      if (!aiPreset || !aiPreset.combatant) {
        console.error('[runBattleSimulation] AI preset not found:', enemyValue);
        const app = getTelegramWebAppSafe();
        app?.showAlert?.("Ошибка: AI пресет не найден");
        return;
      }
      battleRequest.combatants.push({
        team: "B",
        baseId: aiPreset.combatant.baseId,
        creature: aiPreset.combatant.creature,
      });
    }
    
    // Запускаем бой
    try {
      const response = await safeFetch(`${apiUrl}/battle/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...buildAuthHeaders() },
        body: JSON.stringify(battleRequest),
      });
      
      if (!response.success) {
        const app = getTelegramWebAppSafe();
        app?.showAlert?.(response.error || "Ошибка запуска боя");
        return;
      }
      
      loadBattleData(response.data);
      showBattleModal();
      renderBattleResult(response.data);
      await loadBattleHistory();
      
      // Убеждаемся, что рендеринг выполнен после открытия модального окна
      // loadBattleData уже вызывает renderBattle() внутри, но для надежности вызываем renderBattleView
      setTimeout(() => {
        if (battleState && battleState.panels && battleState.panels.length > 0) {
          renderBattleView();
        } else {
          console.warn('[runBattleSimulation] Battle data not ready after loadBattleData, retrying...');
          // Повторная попытка через еще 100ms
          setTimeout(() => {
            if (battleState && battleState.panels && battleState.panels.length > 0) {
              renderBattleView();
            }
          }, 100);
        }
      }, 100);
      
      const errorbornStatus = await checkErrorbornStatus(true);
      const errorbornScreen = document.getElementById("errorborn-screen");
      if (errorbornScreen?.classList.contains("active") && errorbornStatus) {
        renderErrorbornScreen(errorbornStatus);
      }
    } catch (error) {
      console.error("runBattleSimulation error", error);
      const app = getTelegramWebAppSafe();
      app?.showAlert?.("Ошибка запуска боя");
    }
  }

  return runBattleSimulation;
}

